import React, { useEffect, useState } from "react"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import _ from "lodash"
import { Grid, Tooltip, Box, Button, IconButton, Menu, MenuList, MenuItem, Chip, Switch, Typography, Tabs, Tab } from "@material-ui/core"
import MoreVertIcon from "@material-ui/icons/MoreVert"
import { useHistory, useParams } from "react-router-dom"
import { getStatusColor, CASE_ACTIVITY, useInterval } from "../../utils"
import { useSelector, useDispatch } from "react-redux"
import TableView from "../../components/common/TableView/TableView"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { getCharacterCount, setPageNumberCharCount } from "../../actions/billingActions"

const useStyles = makeStyles((theme) => ({
	gridItem: {
		paddingBottom: 12,
	},

	caseDetails: {
		"& .MuiDataGrid-cell": {
			padding: "0 16px",
		},
		"& .MuiChip-root": {
			borderRadius: "4px",
		},
	},
	caseMode: {
		display: "flex",
		alignItems: "center",
		margin: "-7px -10px",
		"& p": { color: theme.palette.status.maintenanceMode },
	},
}))

const CharCount = (props) => {
	const { recordsPerPage, setRecordsPerPage, caseStatus, dateRange, pageNumber, setPageNumber } = props
	const classes = useStyles()
	const history = useHistory()
	const params = useParams()
	const theme = useTheme()
	const caseId = params.id
	const dispatch = useDispatch()
	const [t] = useTranslation("global")

	const characterCount = useSelector((state) => state?.billingReducers?.characterCount)
	const characterCountLoading = useSelector((state) => state?.billingReducers?.characterCountLoading)
	const totalCharacterCount = useSelector((state) => state?.billingReducers?.totalCharacterCount)
	const pageNumberCharCount = useSelector((state) => state?.billingReducers?.pageNumberCharCount)

	const getRows = () => {
		const tableData = characterCount
			? characterCount?.map((caseDetails) => ({
					id: caseDetails?.case_id,
					name: caseDetails?.case_name,
					date: moment(moment.utc(caseDetails?.creation_time)).local().format("MM/DD/YYYY"),
					time: moment(moment.utc(caseDetails?.creation_time)).local().format("HH:mm:ss"),
					total: caseDetails?.total_char_count,
					duplicate: caseDetails?.duplicate_char_count,
					metadata: caseDetails?.metadata || 0,
					billable: caseDetails?.billable_char_count,
					caseStatus: caseDetails?.case_info[0]?.case_mode,
			  }))
			: []
		return tableData
	}

	const getColumns = () => {
		return [
			{
				field: "id",
				sortable: false,
				hide: true,
			},
			{
				field: "name",
				headerName: t("billing.charCount.caseName"),
				sortable: false,
				flex: 0.1,
				width: 250,
			},
			{
				field: "date",
				headerName: t("billing.charCount.date"),
				sortable: false,
				width: 150,
			},
			{
				field: "time",
				headerName: t("billing.charCount.time"),
				sortable: false,
				width: 150,
			},
			{
				field: "total",
				headerName: t("billing.charCount.total"),
				sortable: false,
				width: 180,
				headerAlign: "right",
				align: "right",
			},
			{
				field: "duplicate",
				headerName: t("billing.charCount.duplicate"),
				sortable: false,
				width: 180,
				headerAlign: "right",
				align: "right",
			},
			{
				field: "metadata",
				headerName: t("billing.charCount.metadata"),
				sortable: false,
				width: 150,
				headerAlign: "right",
				align: "right",
			},
			{
				field: "billable",
				headerName: t("billing.charCount.billable"),
				sortable: false,
				width: 180,
				headerAlign: "right",
				align: "right",
			},
			{
				field: "caseStatus",
				headerName: t("billing.charCount.caseStatus"),
				sortable: false,
				width: 180,
				renderCell: (params) => (
					<Chip
						label={params?.row?.caseStatus}
						size="small"
						style={{
							textTransform: "capitalize",
							color: getStatusColor(params?.row?.caseStatus, theme).color,
							backgroundColor: getStatusColor(params?.row?.caseStatus, theme).background,
						}}
					/>
				),
			},
		]
	}

	const handlePageChange = (newPage) => {
		setPageNumber(newPage)
		dispatch(setPageNumberCharCount(newPage))

		const skipCount = newPage === 1 ? 0 : (newPage - 1) * recordsPerPage
		const data = {
			case_id: caseStatus?.case_id,
			start_date: dateRange?.startDate ? moment(dateRange?.startDate).format("YYYY-MM-DD") : "",
			end_date: dateRange?.endDate ? moment(dateRange?.endDate).format("YYYY-MM-DD") : "",
			return_count: recordsPerPage || 5,
			skip_count: skipCount,
		}
		dispatch(getCharacterCount(data))
	}

	const handlePageSizeChange = (newPageSize) => {
		setRecordsPerPage(newPageSize)
	}

	return (
		<div className={classes.caseDetails}>
			<Typography style={{ ...theme.typography.subTitle1, marginBottom: "16px" }}>{t("billing.charCount.charCountLabel")}</Typography>
			<TableView
				columns={getColumns()}
				rows={getRows()}
				isLoading={characterCountLoading}
				pageInfo={{
					page: pageNumberCharCount,
					pagination: true,
					pageSize: recordsPerPage || 5,
					rowsPerPageOptions: [5, 10],
					totalRowCount: totalCharacterCount,
					paginationMode: "server",
					handlePageChange,
					handlePageSizeChange,
				}}
				sortInfo={{
					sortingMode: "server",
					sortModel: [],
					onSortModelChange: () => {},
				}}
				checkboxSelection={false}
				footerInfo={{
					hideFooter: false,
					hideSelectionCount: true,
				}}
				topBarInfo={{
					show: true,
					rightSection: "",
				}}
				totalCountText={`Total count: ${totalCharacterCount || 0}`}
				showDownloadButton={false}
				showColumnsButton={false}
			/>
		</div>
	)
}

export default CharCount
